import { h } from "../core/dom.js";

// Kashtu = Akkadian qaštu, "bow". The weekly-goal motif: the bowstring draws back as the
// week's sessions accumulate (frac 0→1) and the arrow flies once the goal is hit.
// Pure SVG, no timers — rebuild it with a new frac to update (it's inside a stat card
// that re-renders with the dashboard payload).
export function bowProgress(frac, { size = 56 } = {}) {
  const f = Math.max(0, Math.min(1, frac || 0));
  const released = f >= 1;
  // Bow faces right. Stave from (24,8) to (24,56) bulging toward the arrowhead;
  // the string's midpoint (the nock) pulls left as the week fills.
  const pull = released ? 0 : 16 * f;
  const nockX = 24 - pull;
  const stave = "M24 8 C40 20 40 44 24 56";
  const string = `M24 8 L${nockX} 32 L24 56`;
  // Arrow: nocked while drawing, ahead of the bow once released.
  const shaftX1 = released ? 34 : nockX;
  const shaftX2 = released ? 56 : 44;
  return h(
    "svg",
    {
      viewBox: "0 0 64 64", width: size, height: size, "aria-hidden": "true",
      fill: "none", "stroke-linecap": "round", "stroke-linejoin": "round", style: { flex: "none" },
    },
    h("path", { d: stave, stroke: "var(--accent)", "stroke-width": 3.5 }),
    h("path", { d: string, stroke: "var(--accent)", "stroke-width": 1.6, opacity: ".8" }),
    // Arrow in bronze — shaft, head, fletching.
    h("g", { stroke: "var(--bronze)", "stroke-width": 2.6 },
      h("path", { d: `M${shaftX1} 32 L${shaftX2} 32` }),
      h("path", { d: `M${shaftX2 - 6} 27 L${shaftX2} 32 L${shaftX2 - 6} 37` }),
      h("path", { d: `M${shaftX1} 32 L${shaftX1 - 5} 27.5` }),
      h("path", { d: `M${shaftX1} 32 L${shaftX1 - 5} 36.5` })
    ),
    // A small motion hint once the arrow is loosed.
    released ? h("path", { d: "M26 32 L31 32", stroke: "var(--bronze)", "stroke-width": 1.6, opacity: ".5" }) : null
  );
}

/**
 * The same bow, loosed once — for the moment that actually earns it: the finish summary.
 *
 * The dashboard motif draws back over a week and resolves the instant the goal is hit, but a
 * *workout* is what pays for that, and the app has never marked it with anything but an emoji.
 *
 * Pure SVG + CSS, no timers and no JS: it starts painting with the modal, so it can never hold
 * the summary back, and there is nothing to tear down if the user closes the modal mid-flight.
 * The string is drawn as a V whose apex is pulled left; collapsing it with `scaleX` about the
 * stave *is* the release, which is why no path morphing (or SMIL) is needed. Reduced motion gets
 * the resolved end state, still and immediate — the payoff without the movement.
 */
export function bowLoose({ size = 76 } = {}) {
  return h(
    "svg",
    {
      class: "bow-loose", viewBox: "0 0 96 64", width: size, height: (size * 64) / 96,
      "aria-hidden": "true", fill: "none",
      "stroke-linecap": "round", "stroke-linejoin": "round",
    },
    h("path", { class: "bl-stave", d: "M24 8 C40 20 40 44 24 56", stroke: "var(--accent)", "stroke-width": 3.5 }),
    h("path", { class: "bl-string", d: "M24 8 L8 32 L24 56", stroke: "var(--accent)", "stroke-width": 1.6, opacity: ".8" }),
    h("g", { class: "bl-arrow", stroke: "var(--bronze)", "stroke-width": 2.6 },
      h("path", { d: "M8 32 L48 32" }),
      h("path", { d: "M42 27 L48 32 L42 37" }),
      h("path", { d: "M8 32 L3 27.5" }),
      h("path", { d: "M8 32 L3 36.5" })
    ),
    h("g", { class: "bl-trail", stroke: "var(--bronze)", "stroke-width": 1.4 },
      h("path", { d: "M28 26 L38 26" }),
      h("path", { d: "M26 38 L34 38" })
    )
  );
}
